import storage from './storage';
import { activityService } from './activityService';
import { callMatchesUser, normalizeCallOutcomeSlug, isTrackedCall } from '../utils/callOutcomeAnalytics';

function storageKey(userId) {
  return userId ? `crm_streak_u${userId}` : 'crm_streak';
}

export const STREAK_MILESTONES = [
  { days: 3, label: '3-Tage-Streak', emoji: '🔥' },
  { days: 7, label: '1 Woche am Ball', emoji: '⚡' },
  { days: 14, label: '2 Wochen Disziplin', emoji: '💪' },
  { days: 30, label: '30-Tage-Legende', emoji: '🏆' },
];

function dayKeyFromAct(a) {
  if (a?.ts) {
    const d = new Date(a.ts);
    if (!Number.isNaN(d.getTime())) return d.toISOString().slice(0, 10);
  }
  if (a?.date) return convertDeDate(a.date);
  return null;
}

function isApptActivity(a) {
  if (a.type === 'meeting') return true;
  if (a.type === 'call' && normalizeCallOutcomeSlug(a) === 'reached_appt') return true;
  return false;
}

export const streakService = {
  getData(userId) {
    return storage.get(storageKey(userId), { days: {}, goalCallsDay: 10, goalApptsDay: 2 });
  },

  setGoals(userId, goals) {
    const d = this.getData(userId);
    storage.set(storageKey(userId), { ...d, ...goals });
  },

  getGoals(userId) {
    const d = this.getData(userId);
    return { goalCallsDay: d.goalCallsDay || 10, goalApptsDay: d.goalApptsDay || 2 };
  },

  /** Recompute today's counts from activityService and persist day log. */
  refresh(userId, authUsers = []) {
    const data = this.getData(userId);
    const today = new Date().toISOString().slice(0, 10);
    const flat = activityService.getAllFlat();

    const mine = flat.filter(a => {
      const day = dayKeyFromAct(a);
      if (day !== today) return false;
      if (userId) return callMatchesUser(a, userId, authUsers);
      return true;
    });

    const callsToday = mine.filter(a => isTrackedCall(a)).length;
    const apptsToday = mine.filter(isApptActivity).length;

    data.days = data.days || {};
    data.days[today] = { calls: callsToday, appts: apptsToday };
    storage.set(storageKey(userId), data);

    const goals = this.getGoals(userId);
    const streak = this.calcStreak(data);
    const todayHit = callsToday >= goals.goalCallsDay || apptsToday >= goals.goalApptsDay;

    return {
      callsToday,
      apptsToday,
      streak,
      todayHit,
      nextMilestone: this.getNextMilestone(streak),
      currentMilestone: this.getCurrentMilestone(streak),
    };
  },

  calcStreak(data) {
    const { days = {}, goalCallsDay = 10, goalApptsDay = 2 } = data;
    let streak = 0;
    const d = new Date();
    for (let i = 0; i < 365; i++) {
      const dateStr = d.toISOString().slice(0, 10);
      const day = days[dateStr];
      if (!day && i === 0) {
        d.setDate(d.getDate() - 1);
        continue;
      }
      if (!day) break;
      const hit = (day.calls || 0) >= goalCallsDay || (day.appts || 0) >= goalApptsDay;
      if (!hit) break;
      streak++;
      d.setDate(d.getDate() - 1);
    }
    return streak;
  },

  getCurrentMilestone(streak) {
    if (!streak) return null;
    let best = null;
    for (const m of STREAK_MILESTONES) {
      if (streak >= m.days) best = m;
    }
    return best;
  },

  getNextMilestone(streak) {
    for (const m of STREAK_MILESTONES) {
      if (streak < m.days) {
        return { ...m, daysLeft: m.days - streak };
      }
    }
    return null;
  },

  /** Team-Rangliste: Streak pro Nutzer (für Dashboard Team). */
  getTeamStreakBoard(authUsers = []) {
    return authUsers
      .map(u => {
        const r = this.refresh(u.id, authUsers);
        return {
          id: u.id,
          name: u.name || u.email || u.id,
          streak: r.streak,
          callsToday: r.callsToday,
          todayHit: r.todayHit,
        };
      })
      .sort((a, b) => b.streak - a.streak || b.callsToday - a.callsToday);
  },
};

function convertDeDate(str) {
  if (!str) return null;
  const p = str.split('.');
  if (p.length === 3) return `${p[2]}-${p[1].padStart(2, '0')}-${p[0].padStart(2, '0')}`;
  return str;
}
